import React, { memo, useMemo } from 'react';
import { cn } from '@/shared/utils/cn';
import { ListWithDataInHoverProps, ListWithDataInHoverColor } from '../../types';
import { ListWithDataInHoverItem } from '../molecules';
import { DEFAULT_COLORS, normalizeColor } from '../../utils/colors';

/**
 * ListWithDataInHover - Lista de elementos con tooltips que muestran datos
 *
 * Muestra una lista de elementos, cada uno con un indicador de color y un nombre.
 * Al hacer hover sobre un elemento, se muestra un tooltip con el porcentaje asociado.
 *
 * @example
 * ```tsx
 * <ListWithDataInHover
 *   data={[
 *     { name: 'Completado', percent: 60 },
 *     { name: 'En progreso', percent: 30 },
 *     { name: 'Pendiente', percent: 10 }
 *   ]}
 *   colors={['#039B59', '#DC8921', '#D9D9D9']}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Con colores semánticos del sistema
 * <ListWithDataInHover
 *   data={[
 *     { name: 'Éxito', percent: 70 },
 *     { name: 'Advertencia', percent: 20 },
 *     { name: 'Error', percent: 10 }
 *   ]}
 *   colors={[
 *     'var(--ui-color-green-500)',
 *     'var(--ui-color-orange-500)',
 *     'var(--ui-color-red-500)'
 *   ]}
 *   indicatorSize={12}
 *   gap={8}
 * />
 * ```
 */
export const ListWithDataInHover = memo(
  ({
    data,
    colors,
    className,
    disabled = false,
    indicatorSize = 8,
    gap = 4,
    showTooltips = true,
    showPercent = true,
    tooltipPosition = 'over',
    onItemHover,
    onItemLeave,
  }: ListWithDataInHoverProps) => {
    // Validar y normalizar datos
    const normalizedData = useMemo(() => {
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      return data
        .filter((item) => item !== null && typeof item === 'object')
        .map((item) => ({
          name: String(item.name ?? ''),
          percent: Math.max(0, Math.min(100, Number(item.percent) || 0)),
        }));
    }, [data]);

    // Validar y normalizar colores semánticos
    const normalizedColors = useMemo(() => {
      if (!Array.isArray(colors) || colors.length === 0) {
        return DEFAULT_COLORS;
      }

      return colors.map((color) => normalizeColor(color));
    }, [colors]);

    // Normalizar tamaños
    const normalizedIndicatorSize = Math.max(4, Math.min(32, Number(indicatorSize) || 8));
    const normalizedGap = Math.max(0, Math.min(32, Number(gap) || 4));

    // Clases del contenedor
    const containerClasses = cn(
      'ui-flex ui-flex-wrap ui-justify-around ui-w-full',
      disabled && 'ui-opacity-50 ui-pointer-events-none',
      className
    );

    // Estilos del contenedor
    const containerStyle: React.CSSProperties = {
      gap: `${normalizedGap}px`,
      marginTop: '8px',
    };

    // Si no hay datos, retornar null o un estado vacío
    if (normalizedData.length === 0) {
      return (
        <div
          className={containerClasses}
          style={containerStyle}
          role="list"
          aria-label="Lista vacía"
        />
      );
    }

    return (
      <div
        className={cn('ui-imj', containerClasses)}
        style={containerStyle}
        role="list"
        aria-label="Lista con datos en hover"
      >
        {normalizedData.map((item, index) => {
          const color = normalizedColors[index % normalizedColors.length];

          return (
            <ListWithDataInHoverItem
              key={`list-item-${index}-${item.name}`}
              item={item}
              index={index}
              color={color}
              indicatorSize={normalizedIndicatorSize}
              gap={normalizedGap}
              showTooltip={showTooltips}
              showPercent={showPercent}
              tooltipPosition={tooltipPosition}
              onHover={onItemHover}
              onLeave={onItemLeave}
            />
          );
        })}
      </div>
    );
  }
);

ListWithDataInHover.displayName = 'ListWithDataInHover';
